import * as z4 from 'zod/v4/core';
export { ONESTEP_CONFIG_GLOBAL } from './types.js';
const MISSING_CONFIG_MESSAGE = 'window._ONESTEP_CONFIG is not set. The runtime config script must be injected into index.html before application code runs.';
const NON_BROWSER_MESSAGE = 'runtime config read in a non-browser context. Use a test setup to seed window._ONESTEP_CONFIG.';
const formatZodError = (error) => error.issues
    .map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
    return `  - ${path}: ${issue.message}`;
})
    .join('\n');
/**
 * Validate `raw` against `schema` and return the parsed, typed result. Used
 * by every emit point that writes `_ONESTEP_CONFIG` (merge-config script,
 * Vite plugin, test helper) so schema mismatches fail loud *before* they
 * reach the browser.
 *
 * Follows the zod v4 library-author pattern: accepts any `$ZodType` via a
 * bounded generic so the caller's concrete schema type flows through.
 * Reference: https://zod.dev/library-authors#how-to-accept-user-defined-schemas
 */
export const parseRuntimeConfig = (raw, schema) => {
    const result = z4.safeParse(schema, raw);
    if (!result.success) {
        throw new Error(`runtime-config: validation failed\n${formatZodError(result.error)}`);
    }
    return result.data;
};
/**
 * Read the already-validated `window._ONESTEP_CONFIG`. The producer paths
 * (merge-config.mjs + Vite plugin + test helper) all run
 * `parseRuntimeConfig` before writing, so this is a plain typed cast — no
 * runtime validation, no cost per call.
 */
export const getRuntimeConfig = () => {
    if (typeof window === 'undefined')
        throw new Error(NON_BROWSER_MESSAGE);
    const raw = window._ONESTEP_CONFIG;
    if (!raw)
        throw new Error(MISSING_CONFIG_MESSAGE);
    return raw;
};
export const hasRuntimeConfig = () => typeof window !== 'undefined' && Boolean(window._ONESTEP_CONFIG);
/** `targetOrigin` for `parent.postMessage`: the configured `hostOrigin`, else the app's own origin. */
export const getHostOrigin = () => {
    var _a, _b;
    if (typeof window === 'undefined')
        throw new Error(NON_BROWSER_MESSAGE);
    return (_b = (_a = window._ONESTEP_CONFIG) === null || _a === void 0 ? void 0 : _a.hostOrigin) !== null && _b !== void 0 ? _b : window.location.origin;
};
